import { FormEvent, ReactNode, useState } from 'react'
import { customerRegistrationDefaults, customerRegistrationOptions } from '../../mock/legacyCustomerRegistration'
import './LegacyCustomerRegistration.css'

type Gender = '남' | '여' | '남남' | '여여' | '쌍' | ''
type Dialog = 'address' | 'route' | null

const genders: Array<{ value: Exclude<Gender, ''>; label: string; images: Array<'boy' | 'girl'> }> = [
  { value: '남', label: '남아', images: ['boy'] },
  { value: '여', label: '여아', images: ['girl'] },
  { value: '남남', label: '남아 2명', images: ['boy', 'boy'] },
  { value: '여여', label: '여아 2명', images: ['girl', 'girl'] },
  { value: '쌍', label: '혼성 / 쌍둥이', images: ['girl', 'boy'] },
]

function SourceSelect({
  options,
  id,
  name,
  ariaLabel,
  className = '',
}: {
  options: readonly string[]
  id?: string
  name?: string
  ariaLabel: string
  className?: string
}) {
  const isChosen = name === 'customer_type' || name === 'office_id' || name?.startsWith('customer_visit')
  const [selected, setSelected] = useState(options[0] ?? '')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const filteredOptions = options.filter((option) => option.toLocaleLowerCase().includes(query.toLocaleLowerCase()))

  if (isChosen) {
    return <div className={`g_input customer-v2__control customer-v2__chosen-control ${className}`.trim()}>
      <input type="hidden" id={id} name={name} value={selected} />
      <div className={`chosen-container chosen-container-single${open ? ' chosen-with-drop chosen-container-active' : ''}`} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false) }} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false) }}>
        <button type="button" className="chosen-single" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
          <span>{selected}</span><span className="chosen-arrow" aria-hidden="true"><b /></span>
        </button>
        {open && <div className="chosen-drop">
          <div className="chosen-search"><input className="chosen-search-input" aria-label={`${ariaLabel} 검색`} value={query} onChange={(event) => setQuery(event.target.value)} autoFocus /></div>
          <ul className="chosen-results" role="listbox" aria-label={`${ariaLabel} 선택 목록`}>
            {filteredOptions.map((option) => <li className={option === selected ? 'result-selected highlighted' : 'active-result'} role="option" aria-selected={option === selected} key={option}><button type="button" onClick={() => { setSelected(option); setOpen(false); setQuery('') }}>{option}</button></li>)}
          </ul>
        </div>}
      </div>
    </div>
  }

  return <select id={id} name={name} className={`g_input customer-v2__control ${className}`.trim()} aria-label={ariaLabel}>
    {options.map((option) => <option key={option}>{option}</option>)}
  </select>
}

function Field({ className, label, required = false, children }: { className: string; label: string; required?: boolean; children: ReactNode }) {
  return <div className={`g_input_wrap ${required ? 'required ' : ''}customer-v2__field ${className}`}>
    <label className="g_label title customer-v2__field-label">
      {required && <span className="customer-v2__required-mark" aria-hidden="true">*</span>}
      {label}
    </label>
    {children}
  </div>
}

function DateField({ className, label, name }: { className: string; label: string; name: string }) {
  return <Field className={className} label={label}>
    <input className="g_input customer-v2__control g_input_datepicker" name={name} aria-label={label} placeholder={customerRegistrationDefaults.placeholders.date} />
  </Field>
}

export function LegacyCustomerRegistration() {
  const [gender, setGender] = useState<Gender>('')
  const [dialog, setDialog] = useState<Dialog>(null)
  const [submitNotice, setSubmitNotice] = useState('')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitNotice('목업 화면입니다. 입력 내용은 저장되지 않습니다.')
  }

  return <div className="legacy-customer-registration">
    <div id="l_customer_form" className="g_card l_customer_wrapper customer-v2-shell">
      <form id="l_form" className="g_form" onSubmit={submit} noValidate>
        <div className="customer-v2__layout customer-v2__layout--single">
          <section className="customer-v2__left-card" aria-labelledby="customer-form-title">
            <header className="customer-v2__card-header">
              <div className="customer-v2__card-heading"><h1 id="customer-form-title" className="customer-v2__card-title">고객 정보</h1></div>
              <div className="customer-v2__required-guide">* 필수 입력</div>
            </header>

            <div className="l_customer_form_wrapper customer-v2__card-body l_new_regist_form">
              <div className="customer-v2__figma-topbar">
                <div className="customer-v2__figma-title">고객 정보</div>
                <div className="customer-v2__figma-top-fields">
                  <Field className="customer-v2__field--customer-type" label="고객구분" required>
                    <SourceSelect id="customer_type" name="customer_type" ariaLabel="고객구분" options={customerRegistrationOptions.categories} />
                  </Field>
                  <Field className="customer-v2__field--photo-manager" label="지정촬영자">
                    <input id="l_photo_manager" name="photo_manager" className="g_input customer-v2__control" aria-label="지정촬영자" placeholder={customerRegistrationDefaults.placeholders.photoManager} />
                  </Field>
                </div>
              </div>

              <section className="customer-v2__section" aria-label="기본">
                <h2 className="customer-v2__section-title"><span className="customer-v2__section-marker" />기본</h2>
                <div className="customer-v2__row customer-v2__row--basic-detail">
                  <Field className="customer-v2__field--baby-name" label="아기">
                    <input id="l_baby_name" name="baby_name" className="g_input customer-v2__control" aria-label="아기" />
                  </Field>
                  <div className="g_input_wrap gender_icon_wrap customer-v2__field customer-v2__field--gender" role="radiogroup" aria-label="아기 성별">
                    <span className="customer-v2__field-label">성별</span>
                    {genders.map((option) => <label className="gender_label cursor_pointer" key={option.value} title={option.label}>
                      <input type="radio" name="baby_gender_select" value={option.value} aria-label={option.label} checked={gender === option.value} onChange={() => setGender(option.value)} />
                      <span className="gender_label__images">{option.images.map((image, index) => <img key={`${image}-${index}`} src={`/legacy/common/img/customer_contract_form/${image}_${gender === option.value ? 'on' : 'off'}.png`} alt="" />)}</span>
                    </label>)}
                  </div>
                  <Field className="customer-v2__field--blood-type" label="혈액형">
                    <SourceSelect name="baby_bloodtype" ariaLabel="아기 혈액형" options={customerRegistrationOptions.bloodTypes} />
                  </Field>
                  <Field className="customer-v2__field--weight" label="체중">
                    <span className="customer-v2__weight-wrap"><input name="baby_weight" className="g_input customer-v2__control" aria-label="아기 체중" placeholder={customerRegistrationDefaults.placeholders.weight} /><span>kg</span></span>
                  </Field>
                </div>
              </section>

              <section className="customer-v2__section" aria-label="보호자">
                <h2 className="customer-v2__section-title"><span className="customer-v2__section-marker" />보호자</h2>
                <div className="customer-v2__row customer-v2__row--guardian-primary">
                  <Field className="customer-v2__field--parent-name" label="엄마"><input name="mom_name" className="g_input customer-v2__control" aria-label="엄마" /></Field>
                  <Field className="customer-v2__field--parent-name" label="아빠"><input name="dad_name" className="g_input customer-v2__control" aria-label="아빠" /></Field>
                </div>
                <div className="customer-v2__row customer-v2__row--guardian-secondary">
                  <Field className="customer-v2__field--email" label="이메일"><input type="email" name="customer_email" className="g_input customer-v2__control" aria-label="이메일" placeholder={customerRegistrationDefaults.placeholders.email} /></Field>
                  <Field className="customer-v2__field--mom-phone" label="엄마폰">
                    <input name="mom_phone" className="g_input customer-v2__control g_input_phone" aria-label="엄마폰" />
                    <span className="customer-v2__consents"><label><input name="customer_ad" type="checkbox" defaultChecked={customerRegistrationDefaults.advertisementConsent} />광고수신동의</label><label><input name="customer_sms" type="checkbox" defaultChecked={customerRegistrationDefaults.smsConsent} />SMS 수신</label></span>
                  </Field>
                  <Field className="customer-v2__field--dad-phone" label="아빠폰"><input name="dad_phone" className="g_input customer-v2__control g_input_phone" aria-label="아빠폰" /></Field>
                </div>
                <div className="customer-v2__row customer-v2__row--guardian-dates">
                  <Field className="customer-v2__field--parent-birth" label="엄마생일">
                    <SourceSelect name="mom_birthtype" ariaLabel="엄마생일 달력" className="customer-v2__control--calendar-type" options={customerRegistrationOptions.calendarTypes} />
                    <input name="mom_birth" className="g_input customer-v2__control customer-v2__control--date g_input_datepicker" aria-label="엄마생일" placeholder={customerRegistrationDefaults.placeholders.date} />
                  </Field>
                  <Field className="customer-v2__field--parent-birth" label="아빠생일">
                    <SourceSelect name="dad_birthtype" ariaLabel="아빠생일 달력" className="customer-v2__control--calendar-type" options={customerRegistrationOptions.calendarTypes} />
                    <input name="dad_birth" className="g_input customer-v2__control customer-v2__control--date g_input_datepicker" aria-label="아빠생일" placeholder={customerRegistrationDefaults.placeholders.date} />
                  </Field>
                  <DateField className="customer-v2__field--wedding-day" label="결혼기념일" name="customer_weddingday" />
                </div>
              </section>

              <section className="customer-v2__section" aria-label="출산 및 기념일">
                <h2 className="customer-v2__section-title"><span className="customer-v2__section-marker" />출산 · 기념일</h2>
                <div className="customer-v2__row customer-v2__row--birth-dates">
                  <DateField className="customer-v2__field--due-date" label="출산예정일" name="baby_duedate" />
                  <DateField className="customer-v2__field--birth-date" label="출생일" name="baby_birth" />
                  <Field className="customer-v2__field--birth-party" label="돌잔치">
                    <input name="baby_birthparty_day" className="g_input customer-v2__control g_input_datepicker" aria-label="돌잔치 날짜" placeholder={customerRegistrationDefaults.placeholders.date} />
                    <label className="customer-v2__party-choice"><input type="radio" name="baby_birthparty_yn" /> (○)</label>
                    <label className="customer-v2__party-choice"><input type="radio" name="baby_birthparty_yn" /> (⨉)</label>
                  </Field>
                </div>
                <div className="customer-v2__row customer-v2__row--party-location"><Field className="customer-v2__field--party-location" label="돌잔치 장소"><input name="baby_birthparty_loc" className="g_input customer-v2__control" aria-label="돌잔치 장소" placeholder={customerRegistrationDefaults.placeholders.partyLocation} /></Field></div>
              </section>

              <section className="customer-v2__section" aria-label="유입 경로">
                <h2 className="customer-v2__section-title customer-v2__section-title--with-action"><span><span className="customer-v2__section-marker" />유입 경로</span><button className="customer-v2__section-action" type="button" onClick={() => setDialog('route')}>경로 항목 관리</button></h2>
                <div className="customer-v2__row customer-v2__row--acquisition-primary">
                  <Field className="customer-v2__field--visit-primary" label="유입경로" required><SourceSelect name="customer_visit1" ariaLabel="유입경로" options={customerRegistrationOptions.visits.visit1} /></Field>
                  <Field className="customer-v2__field--visit" label="산부인과"><SourceSelect name="customer_visit2" ariaLabel="산부인과" options={customerRegistrationOptions.visits.visit2} /></Field>
                  <Field className="customer-v2__field--visit" label="조리원"><SourceSelect name="customer_visit3" ariaLabel="조리원" options={customerRegistrationOptions.visits.visit3} /></Field>
                </div>
                <div className="customer-v2__row customer-v2__row--acquisition-secondary">
                  <Field className="customer-v2__field--visit" label="추가경로1"><SourceSelect name="customer_visit4" ariaLabel="추가경로1" options={customerRegistrationOptions.visits.visit4} /></Field>
                  <Field className="customer-v2__field--visit" label="추가경로2"><SourceSelect name="customer_visit5" ariaLabel="추가경로2" options={customerRegistrationOptions.visits.visit5} /></Field>
                </div>
              </section>

              <section className="customer-v2__section" aria-label="지점 및 주소">
                <h2 className="customer-v2__section-title"><span className="customer-v2__section-marker" />지점 · 주소</h2>
                <div className="customer-v2__row customer-v2__row--office"><Field className="customer-v2__field--office" label="지점" required><SourceSelect id="l_office_name" name="office_id" ariaLabel="지점" options={customerRegistrationOptions.offices} /></Field></div>
                <div className="customer-v2__row customer-v2__row--address"><Field className="customer-v2__field--address" label="주소"><input name="customer_zipcode" className="g_input customer-v2__control customer-v2__control--zipcode" aria-label="우편번호" placeholder={customerRegistrationDefaults.placeholders.zipcode} readOnly /><button className="customer-v2__address-search" type="button" aria-label="주소 검색" onClick={() => setDialog('address')}>주소 검색</button><input name="customer_addr" className="g_input customer-v2__control customer-v2__control--address" aria-label="기본 주소 및 상세 주소" placeholder={customerRegistrationDefaults.placeholders.address} /></Field></div>
              </section>
            </div>

            <footer className="customer-v2__card-footer"><button id="save_btn" className="g_btn g_btn_submit save customer-v2__save-button" type="submit">신규고객등록</button></footer>
          </section>
        </div>
      </form>
    </div>

    {submitNotice && <p className="customer-v2__submit-notice" role="status">{submitNotice}</p>}
    {dialog && <div className="customer-v2__modal-backdrop" role="presentation"><section className="customer-v2__modal" role="dialog" aria-modal="true" aria-label={dialog === 'address' ? '주소 검색' : '경로 항목 관리'}><h2>{dialog === 'address' ? '주소 검색' : '경로 항목 관리'}</h2><p>{dialog === 'address' ? '주소 검색은 화면 이관 범위에서 목업으로 제공됩니다.' : '경로 항목은 목업 데이터로만 표시됩니다.'}</p><button type="button" onClick={() => setDialog(null)}>닫기</button></section></div>}
  </div>
}
